import Data from "../Data";
import {useContext, useEffect, useRef, useState} from "react";
import Error from "../admin/components/Error";
import SplitColumns from "../account/components/SplitColumns";
import ukMoment from "../../../utils/ukTime";
import AppContext from "../../../context";
import {Link, useParams} from "react-router-dom";
import SumupWidget from "../../components/SumupWidget";
import { hasMarketingConsent, trackMetaEvent } from "../../../utils/metaPixel";

const EXPERIENCE_CART_KEY = 'experience-cart';

const Confirm = ({ basePath = '' }) => {
    const { id } = useParams()
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false)
    const [partialResult, setPartialResult] = useState(null)
    const [sumupCheckoutId, setSumupCheckoutId] = useState(null);
    const [sumupBookingId, setSumupBookingId] = useState(null);
    const [sumupError, setSumupError] = useState(null);
    const sumupPollRef = useRef(null);
    const initiateCheckoutTrackedRef = useRef(false);
    const purchaseTrackedRef = useRef(false);
    const submittingRef = useRef(false);

    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    const eventId = getQueryParam('event');
    const isBulk = getQueryParam('bulk') === 'true';
    const ticketsParam = getQueryParam('tickets');
    const tickets = ticketsParam ? Number(ticketsParam) : 0;

    const [experience, setExperience] = useState([]);
    const [event, setEvent] = useState(null);
    const [cart, setCart] = useState([]);
    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('processor');
    const [discountInput, setDiscountInput] = useState('');
    const [discountCode, setDiscountCode] = useState(null);
    const [discountError, setDiscountError] = useState(null);
    const [marketingConsent, setMarketingConsent] = useState(() => hasMarketingConsent());
    const isSumup = context.domain?.paymentProcessor === 'SumUp';
    const isHybrid = context.domain?.businessType === 'hybrid' || context.domain?.businessType === 'hybrid-new';
    const walletEnabled = !!context.domain?.walletEnabled;
    const totalTickets = isBulk ? cart.reduce((total, item) => total + Number(item.tickets || 0), 0) : tickets;
    const totalPrice = isHybrid
        ? totalTickets * Number(context.domain?.classTokenCost ?? 2)
        : isBulk
        ? cart.reduce((total, item) => total + (Number(item.price ?? experience.price ?? 0) * Number(item.tickets || 0)), 0)
        : totalTickets * Number(experience.price || 0);
    const discountAmount = !isHybrid && discountCode
        ? discountCode.discountType === 'amount'
            ? Math.min(totalPrice, (discountCode.amountOff || 0) / 100)
            : parseFloat((totalPrice * (discountCode.percentageOff || 0) / 100).toFixed(2))
        : 0;
    const discountLabel = discountCode
        ? discountCode.discountType === 'amount'
            ? `£${((discountCode.amountOff || 0) / 100).toFixed(2)} off`
            : `${discountCode.percentageOff}% off`
        : '';
    const finalPrice = Math.max(0, totalPrice - discountAmount);
    const totalPriceMinor = isHybrid ? finalPrice : Math.round(finalPrice * 100);
    const experienceDepositAmount = !isHybrid ? (Number(experience?.depositAmount) || 0) : 0;
    const isDepositMode = experienceDepositAmount > 0;
    const totalDepositAmount = isDepositMode ? (isBulk ? experienceDepositAmount * cart.length : experienceDepositAmount) : 0;
    const chargeAmount = isDepositMode ? totalDepositAmount : finalPrice;
    const chargeAmountMinor = isDepositMode ? Math.round(totalDepositAmount * 100) : totalPriceMinor;
    const balanceDue = isDepositMode ? Math.max(0, finalPrice - totalDepositAmount) : 0;
    const walletBalance = Number((context.domain?.businessType === 'hybrid-new' ? context.user?.classBalance : context.user?.walletBalance) || 0);
    const canUseWallet = walletEnabled && chargeAmountMinor > 0 && walletBalance >= chargeAmountMinor;
    const effectivePaymentMethod = isHybrid ? 'wallet' : paymentMethod;
    const metaPixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;
    const metaCurrency = context.domain?.metaCurrency || 'GBP';
    const showMarketingConsent = !!context.domain?.metaPixelEnabled;

    const trackCheckoutEvent = (eventName, params = {}, eventIdOverride = null) => {
        trackMetaEvent({
            pixelId: metaPixelId,
            eventName,
            eventId: eventIdOverride,
            params: {
                currency: metaCurrency,
                value: Number(finalPrice || 0),
                content_type: 'experience_booking',
                content_ids: isBulk ? cart.map(item => item.eventId).filter(Boolean) : [eventId].filter(Boolean),
                content_name: experience?.name || 'Experience booking',
                num_items: totalTickets,
                ...params
            }
        });
    };

    const updateMarketingConsent = (checked) => {
        setMarketingConsent(checked);
        localStorage.setItem('marketingConsent', checked ? 'true' : 'false');
    };

    const trackSuccessfulBooking = ({ booking: paidBooking, paymentMethod: successfulPaymentMethod }) => {
        if(purchaseTrackedRef.current || !paidBooking?._id) return;
        purchaseTrackedRef.current = true;
        trackCheckoutEvent(finalPrice <= 0 ? 'FreeExperienceBooking' : 'Purchase', {
            payment_method: successfulPaymentMethod,
            booking_reference: paidBooking.reference
        }, 'experience_booking_' + paidBooking._id);
    };

    const applyDiscountCode = async () => {
        setDiscountError(null);
        try {
            const result = await Data.checkDiscountCode(discountInput.trim());
            setDiscountCode(result.data);
        } catch (err) {
            setDiscountError('Invalid or expired discount code.');
        }
    };

    const getRedirectUrl = (payload) => {
        if(!payload) return null;
        if(typeof payload === "string") return payload;
        return payload.url || payload.checkout_url || payload.checkoutUrl || null;
    }

    useEffect(() => {
        const fetchBookingData = async () => {
            try {
                const experience = await Data.fetchExperienceById({ id })
                setExperience(experience.data)
                if(isBulk) {
                    const storedCart = JSON.parse(localStorage.getItem(EXPERIENCE_CART_KEY) || localStorage.getItem(`experience-cart-${id}`) || '[]');
                    if(!storedCart.length) throw new Error('Missing cart');
                    setCart(storedCart);
                } else {
                    if(!eventId) throw new Error('Missing event');
                    const event = await Data.fetchEventById({ id: eventId })
                    setEvent(event.data)
                }
            } catch (error) {
                window.location.href = basePath || "/"
            }
        };

        fetchBookingData();
    }, [eventId, id, isBulk]);

    useEffect(() => {
        if(initiateCheckoutTrackedRef.current || !metaPixelId || totalTickets <= 0 || !experience?.name) return;
        if(isBulk && !cart.length) return;
        if(!isBulk && !eventId) return;

        initiateCheckoutTrackedRef.current = true;
        trackCheckoutEvent('InitiateCheckout');
    }, [metaPixelId, totalTickets, experience?.name, cart.length, isBulk, eventId]);

    const book = async () => {
        // React's `disabled` attribute doesn't take effect until the next render, so two
        // click events fired back-to-back (double-tap, some trackpads) can both start this
        // function before the button visually disables. Guard synchronously instead.
        if(submittingRef.current) return;
        submittingRef.current = true;
        setLoading(true)

        try {
            const booking = isBulk
                ? await Data.createBulkExperienceBookings({
                    bookings: cart.map(item => ({
                        eventId: item.eventId,
                        tickets: item.tickets,
                        notes: notes?.trim() || undefined
                    })),
                    paymentMethod: effectivePaymentMethod,
                    discountCode: discountCode?.code || undefined,
                    marketingConsent: showMarketingConsent && marketingConsent
                })
                : await Data.createExperienceBooking({
                    eventId,
                    tickets,
                    notes: notes?.trim() || undefined,
                    paymentMethod: effectivePaymentMethod,
                    discountCode: discountCode?.code || undefined,
                    marketingConsent: showMarketingConsent && marketingConsent
                });
            if (effectivePaymentMethod === 'wallet' || booking.data?.paymentMethod === 'free') {
                trackSuccessfulBooking({ booking: booking.data?.booking, paymentMethod: booking.data?.paymentMethod || 'wallet' });
                await context.fetchUser();
                if(isBulk) {
                    localStorage.removeItem(EXPERIENCE_CART_KEY);
                    localStorage.removeItem(`experience-cart-${id}`);
                }
                if(booking.data?.partial) {
                    // Some of the bulk sessions were paid and confirmed before one of them
                    // failed (e.g. ran out of wallet balance partway through). Show exactly
                    // what happened instead of redirecting as if everything succeeded -
                    // the customer already has some confirmed bookings and needs to know
                    // which ones still need sorting out.
                    setPartialResult(booking.data);
                    setError(false);
                    return;
                }
                if(isHybrid && basePath) {
                    window.location.href = `${basePath}?success=true`;
                    return;
                }
                const firstBooking = isBulk ? booking.data?.bookings?.[0] : booking.data?.booking;
                window.location.href = isBulk ? `/account?success=true` : `/account/bookings/${firstBooking?._id}?success=true`;
                return;
            }
            if(isSumup) {
                const checkoutId = booking.data?.checkoutId || booking.data?.id || null;
                const bookingId = isBulk ? (booking.data?.bulkReference || checkoutId) : booking.data?.bookingId || null;
                if(!checkoutId || !bookingId) throw new Error("Missing SumUp checkout details");
                if(isBulk) localStorage.setItem(`pendingExperienceBulkCheckoutId-${id}`, checkoutId);
                setSumupCheckoutId(checkoutId);
                setSumupBookingId(bookingId);
            } else {
                const redirectUrl = getRedirectUrl(booking.data);
                if(!redirectUrl) throw new Error("Missing redirect URL");
                localStorage.removeItem(EXPERIENCE_CART_KEY);
                localStorage.removeItem(`experience-cart-${id}`);
                window.location.href = redirectUrl;
            }

            setError(false)
        } catch(error) {
            setError(true)
            if(effectivePaymentMethod === 'wallet') {
                // A wallet charge can complete even when the booking confirmation itself
                // fails, so blindly retrying can charge the customer multiple times for one
                // class before anyone notices. Refresh the balance now so the figure shown
                // below the error is the real, current one, not what was displayed before
                // this attempt - that's the customer's best signal of whether they were
                // actually charged, without needing to leave this page to check.
                context.fetchUser().catch(() => {});
            }
        } finally {
            submittingRef.current = false;
            setLoading(false)
        }
    }

    useEffect(() => {
        if(!sumupCheckoutId || !sumupBookingId) return;
        if(sumupPollRef.current) {
            clearInterval(sumupPollRef.current);
        }

        let cancelled = false;
        const poll = async () => {
            try {
                const result = isBulk
                    ? await Data.verifySumupBulkExperienceBookings(sumupBookingId)
                    : await Data.verifySumupExperienceBooking(sumupBookingId);
                if (!cancelled && result.status === 200) {
                    trackSuccessfulBooking({ booking: result.data?.booking, paymentMethod: 'sumup' });
                    localStorage.removeItem(EXPERIENCE_CART_KEY);
                    localStorage.removeItem(`experience-cart-${id}`);
                    if(isBulk) localStorage.removeItem(`pendingExperienceBulkCheckoutId-${id}`);
                    window.location.href = isBulk ? `/account?success=true` : `/account/bookings/${sumupBookingId}?success=true`;
                }
            } catch (err) {
                // Keep polling while payment is pending; errors will surface on widget callbacks.
            }
        };

        poll();
        sumupPollRef.current = setInterval(poll, 4000);

        return () => {
            cancelled = true;
            if(sumupPollRef.current) {
                clearInterval(sumupPollRef.current);
                sumupPollRef.current = null;
            }
        };
    }, [sumupCheckoutId, sumupBookingId]);

    if(partialResult) {
        return (
            <div className={"p-8"}>
                <div className={"container mx-auto"}>
                    <div className={"grid grid-cols-1 gap-4"}>
                        <h1>Some Bookings Confirmed</h1>
                        <div className={"border rounded-lg p-6"}>
                            <p>{partialResult.message}</p>
                        </div>
                        {partialResult.bookings?.length ? (
                            <div className={"border rounded-lg p-6"}>
                                <h2 className={"text-lg mb-3"}>Confirmed &amp; paid</h2>
                                <ul>
                                    {partialResult.bookings.map(b => (
                                        <li key={b._id}>{b.reference} — {ukMoment(b.bookedOn).format("MMM Do, YYYY")}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        {partialResult.failedBookings?.length ? (
                            <div className={"border rounded-lg p-6"}>
                                <h2 className={"text-lg mb-3 text-red-600"}>Not booked</h2>
                                <ul>
                                    {partialResult.failedBookings.map(b => (
                                        <li key={b._id}>{b.experienceName}</li>
                                    ))}
                                </ul>
                                <p className={"text-sm text-textGrey mt-3"}>
                                    Your wallet was not charged for these. Top up your wallet and try booking them again from the class schedule.
                                </p>
                            </div>
                        ) : null}
                        <Link to={"/account"} className={"inline-block bg-primary py-2 px-4 text-white text-center rounded-md border-0"}>
                            Go to My Account
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={"p-8"}>
            <div className={"container mx-auto"}>
                <div className={"grid grid-cols-1 gap-4 "}>
                    <h1>Booking Summary</h1>

                    <div className={"border rounded-lg p-6"}>
                        <SplitColumns>
                            <p>Your Name</p>
                            <p>{context.user.firstName} {context.user.lastName}</p>
                            <p>Your Email Address</p>
                            <p>{context.user.email}</p>
                            <p>Experience</p>
                            <p>{experience.name}</p>
                            {isBulk ? <>
                                <p>Bookings</p>
                                <p>{cart.map(item => `${item.experienceName ? `${item.experienceName} - ` : ''}${item.label} (${item.tickets})`).join(', ')}</p>
                            </> : <>
                                <p>Date</p>
                                <p>{ukMoment(event?.date).format("MMM Do, YYYY")}</p>
                                <p>Time</p>
                                <p>{ukMoment(event?.date).format("HH:mm A")}</p>
                            </>}
                            <p>Tickets</p>
                            <p>{totalTickets} ticket{totalTickets > 1 ? 's' : ''}</p>
                            <p>{isHybrid ? 'Tokens Required' : 'Total Amount'}</p>
                            {isHybrid ? <p>{finalPrice} token{finalPrice === 1 ? '' : 's'}</p> : discountCode ? <>
                                <p><span style={{ textDecoration: 'line-through', opacity: 0.5 }}>£{totalPrice.toFixed(2)}</span> £{finalPrice.toFixed(2)}</p>
                            </> : <p>£{finalPrice.toFixed(2)}</p>}
                            {discountCode ? <>
                                <p>Discount</p>
                                <p>{discountCode.name} ({discountLabel}) &minus;£{discountAmount.toFixed(2)}</p>
                            </> : null}
                            {isDepositMode ? <>
                                <p>Deposit Due Today</p>
                                <p>£{chargeAmount.toFixed(2)}</p>
                                <p>Balance Due on Day</p>
                                <p>£{balanceDue.toFixed(2)}</p>
                            </> : null}
                        </SplitColumns>
                    </div>

                    {!isHybrid && <div className={"border rounded-lg p-6"}>
                        <label className={"block text-sm font-medium text-textGrey mb-2"}>
                            Discount Code (optional)
                        </label>
                        {discountCode ? (
                            <div className={"flex items-center gap-3"}>
                                <p className={"text-sm text-green-700"}>{discountCode.name} ({discountLabel}) applied</p>
                                <button type="button" onClick={() => { setDiscountCode(null); setDiscountInput(''); }} className={"text-sm text-red-600 border-0 bg-transparent p-0 cursor-pointer"}>Remove</button>
                            </div>
                        ) : (
                            <div className={"flex gap-2"}>
                                <input
                                    type="text"
                                    value={discountInput}
                                    onChange={(e) => setDiscountInput(e.target.value)}
                                    placeholder="Enter code"
                                    className={"border rounded-md p-2 text-sm flex-1"}
                                />
                                <button type="button" onClick={applyDiscountCode} className={"inline-block bg-primary py-2 px-4 text-white text-center rounded-md border-0 text-sm"}>Apply</button>
                            </div>
                        )}
                        {discountError ? <p className={"text-sm text-red-600 mt-1"}>{discountError}</p> : null}
                    </div>}

                    <div className={"border rounded-lg p-6"}>
                        <label htmlFor="booking-notes" className={"block text-sm font-medium text-textGrey mb-2"}>
                            Additional Notes (optional)
                        </label>
                        <textarea
                            id="booking-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={"w-full border rounded-md p-3 min-h-[80px] resize-y"}
                            maxLength={500}
                        />
                        {notes.length > 0 ? (
                            <p className={"text-sm text-textGrey mt-1"}>{notes.length}/500 characters</p>
                        ) : null}
                    </div>

                    {isHybrid ? (
                        <div className={"border rounded-lg p-6"}>
                            <h2 className={"text-lg mb-3"}>Token Payment</h2>
                            <p className={"text-sm text-textGrey"}>Current balance: {walletBalance} token{walletBalance === 1 ? '' : 's'}</p>
                            {!canUseWallet ? (
                                <p className={"text-sm text-red-600 mt-3"}>
                                    You need {finalPrice} token{finalPrice === 1 ? '' : 's'} to book this class.
                                </p>
                            ) : null}
                        </div>
                    ) : walletEnabled && totalPriceMinor > 0 ? (
                        <div className={"border rounded-lg p-6"}>
                            <h2 className={"text-lg mb-3"}>Payment Method</h2>
                            <div className={"grid gap-3"}>
                                <label className={"flex items-start gap-3"}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="wallet"
                                        checked={paymentMethod === 'wallet'}
                                        onChange={() => setPaymentMethod('wallet')}
                                        disabled={!canUseWallet}
                                    />
                                    <span>
                                        <strong>Use wallet credit</strong>
                                        <span className={"block text-sm text-textGrey"}>
                                            Pay £{chargeAmount.toFixed(2)}{isDepositMode ? ' deposit' : ''} from your wallet. Current balance: £{(walletBalance / 100).toFixed(2)}
                                        </span>
                                    </span>
                                </label>

                                <label className={"flex items-start gap-3"}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="processor"
                                        checked={paymentMethod === 'processor'}
                                        onChange={() => setPaymentMethod('processor')}
                                    />
                                    <span>
                                        <strong>Pay online</strong>
                                        <span className={"block text-sm text-textGrey"}>Pay £{chargeAmount.toFixed(2)}{isDepositMode ? ' deposit' : ''} during checkout.</span>
                                    </span>
                                </label>
                            </div>

                            {!canUseWallet ? (
                                <p className={"text-sm text-textGrey mt-3"}>
                                    You need £{chargeAmount.toFixed(2)} in your wallet to pay this way. <Link to="/account/wallet">Top up your wallet</Link>.
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {showMarketingConsent ? (
                        <label className={"flex items-start gap-3 border rounded-lg p-6 cursor-pointer"}>
                            <input
                                type="checkbox"
                                checked={marketingConsent}
                                onChange={(e) => updateMarketingConsent(e.target.checked)}
                            />
                            <span>
                                <strong>Marketing consent</strong>
                                <span className={"block text-sm text-textGrey"}>I agree to receive marketing and personalised offers.</span>
                            </span>
                        </label>
                    ) : null}

                    <div>
                    <button disabled={loading || !!sumupCheckoutId || (isHybrid && !canUseWallet)}
                                className={"inline-block bg-primary py-2 px-4 text-white text-center rounded-md mt-6 border-0"}
                                onClick={book}>{loading ? 'Redirecting...' : sumupCheckoutId ? 'Payment Ready' : effectivePaymentMethod === 'wallet' ? 'Confirm Booking' : 'Continue to Payment'}</button>
                    </div>

                    {sumupCheckoutId ? (
                        <div className={"mt-6"}>
                            <SumupWidget
                                checkoutId={sumupCheckoutId}
                                onSuccess={async () => {
                                    try {
                                        setSumupError(null);
                                        const result = isBulk
                                            ? await Data.verifySumupBulkExperienceBookings(sumupBookingId)
                                            : await Data.verifySumupExperienceBooking(sumupBookingId);
                                        if (result.status === 200) {
                                            trackSuccessfulBooking({ booking: result.data?.booking, paymentMethod: 'sumup' });
                                            localStorage.removeItem(EXPERIENCE_CART_KEY);
                                            localStorage.removeItem(`experience-cart-${id}`);
                                            if(isBulk) localStorage.removeItem(`pendingExperienceBulkCheckoutId-${id}`);
                                            window.location.href = isBulk ? `/account?success=true` : `/account/bookings/${sumupBookingId}?success=true`;
                                        }
                                    } catch (err) {
                                        setSumupError('There was an issue verifying your payment. Please refresh.');
                                    }
                                }}
                                onError={(message) => {
                                    setSumupError(message || 'Payment failed');
                                }}
                            />
                        </div>
                    ) : null}

                    {sumupError ? <Error message={sumupError} /> : null}

                    {error ? <Error message={effectivePaymentMethod === 'wallet'
                        ? "There was a problem confirming your booking. Your wallet balance above has been refreshed - if it's lower than expected, your wallet may already have been charged, so please contact us before trying again rather than repeating the payment."
                        : "There was an error creating your booking, please try again."}/> : null}
                </div>
            </div>
        </div>
    )
}

export default Confirm
